import * as React from 'react';
import {AnimatePresence} from '@sail/engine';
import {ErrorMessage, Overlay} from '@stripe-internal/connect-embedded-lib';
import {message, useIntl} from '@sail/intl';
import {useMetrics} from '@sail/observability';
import {DeferredPromise} from '../../../utils/DeferredPromise';
import {ExternalAccountConfirmationWrapper} from './ExternalAccountConfirmationWrapper';
import {useAuthContext} from '../../../data-layer-client/AuthContext/AuthContext';
import useRequireExternalAccountVerificationMutation from './queries/useRequireExternalAccountVerificationMutation';
import {useSubmerchantAuthentication} from '../../../data-layer-client/SubmerchantAuthenticationContext';

type ReadyState = 'idle' | 'loading' | 'ready' | 'challenge' | 'error';
type RequiredKey = 'external_account';
export type KeyProviderContextProps = {
  state: ReadyState;
  requestKey: (keyType: RequiredKey) => Promise<KeyProviderResponse>;
};
export type KeyProviderResponse = 'success' | 'error';

export const messages = {
  genericError: message({
    id: 'connectEmbed.keyProvider.genericError',
    description:
      'Generic error message when we are unable to fetch information about the current external account',
    defaultMessage: "We couldn't load your account information.",
  }),
  verifyAccountInformation: message({
    id: 'connectEmbed.keyProvider.verifyAccountInformation',
    description: 'Title for external account challenge',
    defaultMessage: 'Verify account information',
  }),
};

export const KeyProviderContext: React.Context<KeyProviderContextProps> =
  React.createContext<KeyProviderContextProps>({
    state: 'idle',
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    requestKey: async () => 'success' as KeyProviderResponse,
  });
KeyProviderContext.displayName = 'KeyProviderContext';

type KeyProviderWrapperState = {
  readyState: ReadyState;
  deferredPromise?: DeferredPromise<KeyProviderResponse>;
  challenge?: string;
  identityAuthEligible?: boolean;
  accountNumberVerificationEligible?: boolean;
  error?: Error;
};

const KeyProviderWrapper = ({children}: {children: any}) => {
  const [state, setState] = React.useState<KeyProviderWrapperState>({
    readyState: 'idle',
  });
  const {authInfo} = useAuthContext();
  const clientSecret = authInfo?.clientSecret;
  const {isApiKeyOverride} = useSubmerchantAuthentication();
  const metrics = useMetrics();

  const [mutateRequireExternalAccountVerification] =
    useRequireExternalAccountVerificationMutation();

  const requestKey = React.useCallback(
    async (requiredKey: string): Promise<KeyProviderResponse> => {
      setState({readyState: 'loading'});

      try {
        switch (requiredKey) {
          case 'external_account':
            const {data} = await mutateRequireExternalAccountVerification({
              variables: {
                input: {
                  // If an api key override is set, we're running onboarding inside:
                  // EiH, express, or embedded login as. In all of these cases we should not attempt
                  // to fetch the account session using client secret.
                  client_secret: isApiKeyOverride ? null : clientSecret,
                },
              },
            });
            const requestKeyComplete =
              new DeferredPromise<KeyProviderResponse>();
            metrics.increment(
              'connect.auth.external_account_verification.requires_verification.check',
            );

            const {
              requires_verification: requiresVerification,
              challenge,
              identity_auth_eligible: identityAuthEligible,
              account_number_verification_eligible:
                accountNumberVerificationEligible,
            } = data?.v1ConnectAuthRequiresExternalAccountVerification || {};
            if (!requiresVerification) {
              setState({readyState: 'ready'});
              metrics.increment(
                'connect.auth.external_account_verification.requires_verification.not_required',
              );

              return Promise.resolve<KeyProviderResponse>('success');
            } else {
              setState({
                readyState: 'challenge',
                deferredPromise: requestKeyComplete,
                challenge: challenge || undefined,
                identityAuthEligible: identityAuthEligible || undefined,
                accountNumberVerificationEligible:
                  accountNumberVerificationEligible || undefined,
              });
              metrics.increment(
                'connect.auth.external_account_verification.requires_verification.required',
              );

              return requestKeyComplete.promise;
            }
          default:
            throw new Error(`Unknown key type: ${requiredKey}`);
        }
      } catch (error) {
        const dialogClosed = new DeferredPromise<KeyProviderResponse>();

        setState({
          readyState: 'error',
          deferredPromise: dialogClosed,
          error: error instanceof Error ? error : new Error(String(error)),
        });

        return dialogClosed.promise;
      }
    },
    [
      mutateRequireExternalAccountVerification,
      metrics,
      clientSecret,
      isApiKeyOverride,
    ],
  );

  return (
    <KeyProviderContext.Provider value={{state: state.readyState, requestKey}}>
      <KeyProvider state={state} setState={setState}>
        {children}
      </KeyProvider>
    </KeyProviderContext.Provider>
  );
};

const OverlayWrapper = ({
  children,
  onClose,
}: {
  children: any;
  onClose: () => void;
}) => {
  return (
    <AnimatePresence>
      <Overlay handleClose={onClose}>{children}</Overlay>
    </AnimatePresence>
  );
};

const KeyProviderInner = ({
  state,
  setState,
}: {
  state: KeyProviderWrapperState;
  setState: (state: KeyProviderWrapperState) => void;
}) => {
  const intl = useIntl();
  const {
    readyState,
    challenge,
    identityAuthEligible,
    accountNumberVerificationEligible,
  } = state;

  const handleSuccess = () => {
    state.deferredPromise && state.deferredPromise.resolve('success');
    setState({readyState: 'ready'});
  };

  const handleClose = () => {
    if (state.readyState !== 'loading') {
      state.deferredPromise && state.deferredPromise.resolve('error');
      setState({readyState: 'idle'});
    }
  };

  if (readyState === 'error') {
    return (
      <OverlayWrapper onClose={handleClose}>
        <ErrorMessage>{intl.formatMessage(messages.genericError)}</ErrorMessage>
      </OverlayWrapper>
    );
  } else if (readyState === 'challenge' && challenge) {
    return (
      <OverlayWrapper onClose={handleClose}>
        <Overlay.Title>
          {intl.formatMessage(messages.verifyAccountInformation)}
        </Overlay.Title>
        <ExternalAccountConfirmationWrapper
          challengeId={challenge}
          identityAuthEligible={!!identityAuthEligible}
          accountNumberVerificationEligible={
            !!accountNumberVerificationEligible
          }
          onSuccess={handleSuccess}
          onClose={handleClose}
        />
      </OverlayWrapper>
    );
  } else {
    return null;
  }
};

const KeyProvider = ({
  state,
  setState,
  children,
}: {
  state: KeyProviderWrapperState;
  setState: (state: KeyProviderWrapperState) => void;
  children: any;
}) => {
  return (
    <>
      {children}
      <KeyProviderInner state={state} setState={setState} />
    </>
  );
};

export default KeyProviderWrapper;
